import { useParams } from '@tanstack/react-router';
import { ArrowRight, MapPin, Phone, Smartphone, UsersRound } from 'lucide-react';
import StoreBadges from 'app/javascript/components/shared/StoreBadges';
import { usePublicCompany } from 'app/javascript/queries/useCompanies';
import { buildWhatsAppUrl } from 'app/javascript/utils/whatsapp';

export default function JoinAppLanding() {
  const { slug, token } = useParams({ strict: false }) as { slug: string; token: string };
  const { data: company } = usePublicCompany(slug, { retry: false });
  const appUrl = `app.blackbelts://join/${encodeURIComponent(slug)}/${encodeURIComponent(token)}`;
  const region = [company?.city, company?.state].filter(Boolean).join(' - ');
  const location = [company?.address, region].filter(Boolean).join(' · ');
  const whatsappUrl = buildWhatsAppUrl(company?.phone, '');

  return (
    <main className="relative flex min-h-screen items-start overflow-hidden bg-slate-950 px-5 py-6 text-white sm:items-center sm:py-12">
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -left-24 top-12 h-72 w-72 rounded-full bg-yellow-400/10 blur-3xl" />
        <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-yellow-400/5 blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.04)_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>

      <section className="relative mx-auto w-full max-w-xl">
        <div className="mb-8 flex items-center gap-3">
          <img src="/blackbelts-mark@2x.png" alt="" className="h-12 w-12 rounded-xl bg-black p-1.5" />
          <div>
            <p className="font-bebas text-3xl tracking-wide">BlackBelts</p>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-yellow-400">Jiu-Jitsu no bolso</p>
          </div>
        </div>

        <div className="rounded-3xl border border-slate-800 bg-slate-900/80 p-6 shadow-2xl shadow-black/30 backdrop-blur sm:p-9">
          {company && (
            <div className="mb-7 border-b border-slate-800 pb-6">
              <div className="flex items-center gap-4">
                {company.logo_url ? (
                  <img
                    src={company.logo_url}
                    alt={`Logo da ${company.name}`}
                    className="h-16 w-16 shrink-0 rounded-2xl border border-slate-700/60 object-cover"
                  />
                ) : (
                  <div
                    aria-hidden="true"
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl border border-slate-700/60 bg-slate-800 font-bebas text-3xl text-yellow-400"
                  >
                    {company.name.trim().charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="min-w-0">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-yellow-400">Time do convite</p>
                  <h2 className="mt-1 text-balance font-bebas text-3xl leading-none tracking-wide text-white sm:text-4xl">
                    {company.name}
                  </h2>
                </div>
              </div>

              {(location || company.phone) && (
                <div className="mt-5 grid gap-2.5 text-sm text-slate-300">
                  {location && (
                    <p className="flex items-start gap-2.5 leading-relaxed">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" aria-hidden="true" />
                      {location}
                    </p>
                  )}
                  {company.phone && whatsappUrl && (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Conversar com ${company.name} no WhatsApp: ${company.phone}`}
                      className="flex min-h-11 w-fit items-center gap-2.5 rounded-lg text-yellow-400 underline underline-offset-4 transition-colors hover:text-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400"
                    >
                      <Phone className="h-4 w-4 shrink-0 text-yellow-400" aria-hidden="true" />
                      {company.phone}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          <p className="mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-yellow-400">
            <UsersRound className="h-4 w-4" aria-hidden="true" />
            Convite da equipe
          </p>
          <h1 className="text-balance font-bebas text-5xl italic leading-none tracking-tight sm:text-6xl">
            Entre para a equipe pelo app
          </h1>
          <p className="mt-5 max-w-lg text-base leading-relaxed text-slate-300">
            O BlackBelts vai guardar este convite, pedir seu acesso se necessário e concluir sua inscrição automaticamente.
          </p>

          <a
            href={appUrl}
            className="mt-8 flex min-h-14 w-full items-center justify-center gap-3 rounded-xl bg-yellow-400 px-5 py-4 font-bebas text-lg uppercase tracking-wide text-slate-950 shadow-lg shadow-yellow-400/20 transition-colors hover:bg-yellow-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
          >
            <Smartphone className="h-5 w-5" aria-hidden="true" />
            Abrir o BlackBelts
            <ArrowRight className="h-5 w-5" aria-hidden="true" />
          </a>

          <div className="my-7 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            <span className="h-px flex-1 bg-slate-800" />
            Ainda não tem o app?
            <span className="h-px flex-1 bg-slate-800" />
          </div>

          <StoreBadges size="sm" className="justify-center" />
          <p className="mt-5 text-center text-sm leading-relaxed text-slate-400">
            Depois de instalar, abra este link novamente para continuar com o mesmo convite.
          </p>
        </div>
      </section>
    </main>
  );
}
